import React, {useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import uniqueId from 'lodash/uniqueId';
import * as d3 from 'd3';

import styles from './TreeMapSvg.module.scss';

export const TreeMapSvg = ({width, height, data, tile, sumFunc, color}) => {
  const chartRef = useRef();

  useEffect(() => {
    const treemap = data => d3.treemap()
      .tile(d3[tile])
      .size([width, height])
      .padding(1)
      .round(true)(d3.hierarchy(data)
        .sum(d => sumFunc ? (sumFunc(d.size) || d.size) : d.size)
        .sort((a, b) => b.value - a.value));

    const format = d3.format(',d');

    const chart = container => {
      const root = treemap(data);

      const svg = d3.select(container).append('svg')
        .attr('viewBox', [0, 0, width, height])
        .style('font', '10px sans-serif');

      const leaf = svg.selectAll('g')
        .data(root.leaves())
        .join('g')
        .attr('transform', d => `translate(${d.x0},${d.y0})`);

      leaf.append('title')
        .text(d => `${d.ancestors().reverse().map(d => d.data.name).join('/')}\n${format(d.data.size)}`);

      leaf.append('rect')
        .attr('id', d => (d.leafUid = uniqueId('leaf')))
        .attr('href', d => `#${d.leafUid}`)
        .attr('fill', d => {while (d.depth > 1) d = d.parent; return color(d.data.name);})
        .attr('fill-opacity', 0.6)
        .attr('width', d => d.x1 - d.x0)
        .attr('height', d => d.y1 - d.y0);

      leaf.append('clipPath')
        .attr('id', d => (d.clipUid = uniqueId('clip')))
        .append('use')
        .attr('xlink:href', d => d3.select(`#${d.leafUid}`).attr('href'));

      leaf.append('text')
        .attr('clip-path', d => d.clipUid)
        .selectAll('tspan')
        // @todo: improve text wrapping using d3plus
        .data(d => d.data.name.split(/(?=[A-Z][^A-Z])/g).concat(format(d.data.size)))
        .join('tspan')
        .attr('x', 3)
        .attr('y', (d, i, nodes) => `${(i === nodes.length - 1) * 0.3 + 1.1 + i * 0.9}em`)
        .attr('fill-opacity', (d, i, nodes) => i === nodes.length - 1 ? 0.7 : null)
        .text(d => d);

      return svg.node();
    };

    const element = chartRef.current;
    chart(element);

    return () => d3.select(element).selectAll('*').remove();
  }, [width, height, data, tile, sumFunc, color]);

  return <div className={styles.container} ref={chartRef} />;
};

TreeMapSvg.propTypes = {
  color: PropTypes.func,
  data: PropTypes.object.isRequired,
  height: PropTypes.number,
  sumFunc: PropTypes.func,
  tile: PropTypes.string,
  width: PropTypes.number,
};

TreeMapSvg.defaultProps = {
  color: d3.scaleOrdinal(d3.schemeCategory10),
  height: 500,
  tile: 'treemapSquarify',
  width: 960,
};
